import { useState } from 'react';
import { Mail, MonitorSmartphone, Send, Trash2, UserRound, X } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Button } from '@/components/button';
import { Badge } from '@/components/badge';
import { InviteDialog } from '@/components/invite-dialog';
import { Switch } from '@/components/switch';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { EntityAvatar } from '@/components/entity-avatar';
import { IconButton } from '@/components/icon-button';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  PORTAL_PERMISSIONS,
  usePortalUsers,
  useRemovePortalUser,
  useUpdatePortalUser,
} from '@/lib/permissions';
import {
  useInvitations,
  useRevokeInvitation,
} from '@/lib/invitations';
import { formatDateLong } from '@/lib/format';
import type { ClientData, PortalUserData } from '@/types/api';

/**
 * Admin control of this client's portal users and their per-user access. Each
 * toggle grants/revokes one permission for that specific user; the portal
 * reflects it on their next load. Invited-but-not-yet-accepted people show as
 * pending until they set a password.
 */
export function ClientPortalUsers({ client }: { client: ClientData }) {
  const { data: users, isLoading } = usePortalUsers(client.id);
  const { data: allInvitations } = useInvitations();
  const [inviteOpen, setInviteOpen] = useState(false);

  const pending = (allInvitations ?? []).filter(
    (inv) => inv.role === 'client_user' && inv.client_id === client.id,
  );
  const isEmpty = (!users || users.length === 0) && pending.length === 0;

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <MonitorSmartphone className="size-4 text-[var(--color-foreground-muted)]" />
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Portal users</h2>
          </div>
          <p className="mt-1 text-xs text-[var(--color-foreground-muted)]">
            Grant each person their own access to {client.name}'s portal. The Dashboard is always
            available; everything else is opt-in per user.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <Send className="size-4" /> Invite user
        </Button>
      </CardHeader>
      <CardBody className="!p-0">
        {isLoading ? (
          <div className="p-5">
            <SkeletonList rows={2} />
          </div>
        ) : isEmpty ? (
          <div className="p-5">
            <EmptyState
              icon={UserRound}
              title="No portal users yet"
              description="Invite someone from this client to give them portal access, then set what they can see here."
              action={
                <Button onClick={() => setInviteOpen(true)}>
                  <Send className="size-4" /> Invite user
                </Button>
              }
            />
          </div>
        ) : (
          <>
            {pending.length > 0 ? (
              <div className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/40 px-5 py-3">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-[var(--color-foreground-muted)]">
                  Pending invitations
                </p>
                <ul className="space-y-1.5">
                  {pending.map((inv) => (
                    <PendingInvitationRow key={inv.id} id={inv.id} name={inv.name} email={inv.email} expiresAt={inv.expires_at} />
                  ))}
                </ul>
              </div>
            ) : null}
            {users && users.length > 0 ? (
              <ul className="divide-y divide-[var(--color-border)]">
                {users.map((user) => (
                  <PortalUserRow key={user.id} clientId={client.id} user={user} />
                ))}
              </ul>
            ) : null}
          </>
        )}
      </CardBody>

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={`Invite to ${client.name}'s portal`}
        role="client_user"
        clientId={client.id}
        hint="They'll get an email with a link to set a password. They start with no access; grant what they can see here once they accept."
      />
    </Card>
  );
}

function PendingInvitationRow({
  id,
  name,
  email,
  expiresAt,
}: {
  id: number;
  name: string;
  email: string;
  expiresAt: string;
}) {
  const revoke = useRevokeInvitation();
  const toast = useToast();

  return (
    <li className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <Mail className="size-3.5 shrink-0 text-[var(--color-foreground-muted)]" />
        <span className="truncate text-sm font-medium text-[var(--color-foreground)]">{name}</span>
        <Badge tone="warning">Pending</Badge>
        <span className="truncate text-xs text-[var(--color-foreground-muted)]">
          {email} · expires {formatDateLong(expiresAt)}
        </span>
      </div>
      <IconButton
        aria-label={`Revoke invitation for ${name}`}
        onClick={async () => {
          await revoke.mutateAsync(id);
          toast.success(`Invitation for ${name} revoked`);
        }}
      >
        <X className="size-4" />
      </IconButton>
    </li>
  );
}

function PortalUserRow({ clientId, user }: { clientId: number; user: PortalUserData }) {
  const update = useUpdatePortalUser(clientId);
  const remove = useRemovePortalUser(clientId);
  const confirm = useConfirm();
  const toast = useToast();

  async function toggle(key: string, label: string, next: boolean) {
    const permissions = next
      ? [...user.permissions, key]
      : user.permissions.filter((p) => p !== key);
    await update.mutateAsync({ userId: user.id, patch: { permissions } });
    toast.success(`${label} ${next ? 'granted to' : 'revoked from'} ${user.name}`);
  }

  return (
    <li className="px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <EntityAvatar name={user.name} src={user.avatar_thumb_url ?? undefined} />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-[var(--color-foreground)]">{user.name}</p>
            <p className="truncate text-xs text-[var(--color-foreground-muted)]">
              {user.title_at_client ? `${user.title_at_client} · ` : ''}
              {user.email}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={async () => {
            const ok = await confirm({
              title: `Remove ${user.name}'s portal access?`,
              body: 'They will no longer be able to log into this portal. Their account is kept.',
              confirmLabel: 'Remove access',
              tone: 'danger',
            });
            if (ok) {
              await remove.mutateAsync(user.id);
              toast.success(`Removed ${user.name}'s access`);
            }
          }}
          aria-label={`Remove ${user.name}`}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-danger)]"
        >
          <Trash2 className="size-4" />
        </button>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {PORTAL_PERMISSIONS.map((permission) => {
          const granted = user.permissions.includes(permission.key);
          return (
            <label
              key={permission.key}
              className="flex items-center justify-between gap-3 rounded-md border border-[var(--color-border)] px-3 py-2"
            >
              <div className="min-w-0">
                <span className="block text-xs font-medium text-[var(--color-foreground)]">
                  {permission.group}
                </span>
                <span className="block text-[11px] text-[var(--color-foreground-muted)]">
                  {permission.label}
                </span>
              </div>
              <Switch
                checked={granted}
                disabled={update.isPending}
                label={`${permission.group} for ${user.name}`}
                onChange={(next) => void toggle(permission.key, permission.group, next)}
              />
            </label>
          );
        })}
      </div>
    </li>
  );
}
